// src/components/Shop/AllProducts.jsx

import React, { useEffect, useState } from "react";
import {
  Button,
  Popover,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AiOutlineDelete, AiOutlineEye, AiOutlineEdit } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  getAllProductsShop,
  deleteProduct,
} from "../../redux/actions/product";
import Loader from "../Layout/Loader";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../../server";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const AllProducts = () => {
  const dispatch = useDispatch();
  const { products = [], isLoading = false } = useSelector(
    (state) => state.products || {}
  );
  const { seller = {} } = useSelector((state) => state.seller || {});

  // SINGLE product deletion states
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // MULTIPLE product deletion states
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [openDeleteMultipleDialog, setOpenDeleteMultipleDialog] = useState(false);

  // BULK Upload/Edit states
  const [bulkActionType, setBulkActionType] = useState(null); // 'upload' or 'edit'
  const [bulkPopupOpen, setBulkPopupOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isBulkLoading, setIsBulkLoading] = useState(false);

  // Filter and Sort persistence
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);

  useEffect(() => {
    // Load saved filters only if navigating back (not on fresh page load)
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    const isPageReload = navigationEntry && navigationEntry.type === 'reload';
    
    if (!isPageReload) {
      const savedFilter = sessionStorage.getItem('shopAllProductsFilterModel');
      const savedSort = sessionStorage.getItem('shopAllProductsSortModel');
      
      if (savedFilter) {
        setFilterModel(JSON.parse(savedFilter));
      }
      if (savedSort) {
        setSortModel(JSON.parse(savedSort));
      }
    } else {
      // Clear filters on page reload
      sessionStorage.removeItem('shopAllProductsFilterModel');
      sessionStorage.removeItem('shopAllProductsSortModel');
    }
  }, []);

  useEffect(() => {
    if (seller._id) {
      dispatch(getAllProductsShop(seller._id));
    }
  }, [dispatch, seller._id]);

  // ======================
  // === SINGLE DELETE ====
  // ======================
  const handleDelete = async () => {
    if (!productToDelete) return;
    await dispatch(deleteProduct(productToDelete.id));
    setOpenDeleteDialog(false);
    setProductToDelete(null);
    toast.success("Product deleted successfully!");
  };

  // ======================
  // === MULTIPLE DELETE =
  // ======================
  const handleDeleteMultiple = () => {
    // If no selected items, show toast
    if (!selectedProductIds || selectedProductIds.length === 0) {
      toast.error("No products selected for deletion!");
      return;
    }
    setOpenDeleteMultipleDialog(true);
  };

  const confirmDeleteMultiple = async () => {
    try {
      // Delete all selected products in parallel
      await Promise.all(
        selectedProductIds.map((id) => dispatch(deleteProduct(id)))
      );
      toast.success("Selected products deleted successfully!");
    } catch (error) {
      toast.error("An error occurred while deleting products.");
    } finally {
      setOpenDeleteMultipleDialog(false);
      // Clear selection after deleting
      setSelectedProductIds([]);
    }
  };

  // =========================
  // === BULK UPLOAD/EDIT ===
  // =========================
  const handleBulkAction = async () => {
    if (!selectedFile || !bulkActionType) {
      toast.error("Please select a file and confirm the action.");
      return;
    }
    setIsBulkLoading(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    const url =
      bulkActionType === "upload"
        ? `${server}/product/bulk-upload/${seller._id}`
        : `${server}/product/bulk-edit/${seller._id}`;

    try {
      const response = await axios.post(url, formData, {
        withCredentials: true,
      });

      toast.success(response.data.message);
      setBulkPopupOpen(false);
      setSelectedFile(null);
      dispatch(getAllProductsShop(seller._id)); // Refresh the products after bulk action
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Bulk action failed.";
      toast.error(errorMessage);
    } finally {
      setIsBulkLoading(false);
    }
  };

  // ==============================
  // === EXCEL DOWNLOAD (HYBRID) ==
  // ==============================
  const handleDownloadAllProductsHybrid = async () => {
    if (!seller._id) {
      toast.error("Seller ID is missing.");
      return;
    }

    try {
      // Fetch product data
      const response = await axios.get(
        `${server}/product/get-all-products-data/${seller._id}`,
        {
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.products.length > 0) {
        const productsData = response.data.products;

        // 1) Determine the maximum number of quantity slabs
        const maxSlabs = Math.max(
          ...productsData.map((product) =>
            product.quantitySlabs ? product.quantitySlabs.length : 0
          )
        );

        // 2) Base columns
        const baseColumns = [
          "ProductId",
          "Name",
          "Description",
          "Category",
          "Tags",
          "PricePerQty",
          "GSTPercentage",
          "CommissionRate",
          "HSNCode",
          "LeadTime",
          "Stock",
          "StockAvailability",
          "HasVariants",
          "VariantTypes",
          "VariantItems",
          "VariantStock",
          "Images",
          "Shop",
          "SoldOut",
          "CreatedAt",
          "MinimumOrderQuantity", 
          "ServiceableLocation",
        ];

        // 3) Generate QuantitySlab columns
        const quantitySlabHeaders = [];
        for (let i = 1; i <= maxSlabs; i++) {
          quantitySlabHeaders.push(
            `QuantitySlab${i}_Min`,
            `QuantitySlab${i}_Max`,
            `QuantitySlab${i}_OriginalPrice`,
            `QuantitySlab${i}_DiscountPrice`,
            `QuantitySlab${i}_TotalPrice`
          );
        }

        const allColumns = [...baseColumns, ...quantitySlabHeaders];

        // 4) Map product data for Excel
        const dataToExport = productsData.map((product) => {
          const rowData = {
            ProductId: product._id ? product._id.toString() : "",
            Name: product.name,
            Description: product.description,
            Category: product.category,
            Tags: product.tags,
            PricePerQty: product.pricePerQty,
            GSTPercentage: product.gstPercentage,
            CommissionRate: product.commissionRate,
            HSNCode: product.hsnCode,
            LeadTime: product.leadTime,
            Stock: product.stock,
            StockAvailability: product.stockAvailability,
            HasVariants: product.hasVariants ? "TRUE" : "FALSE",
            VariantTypes: product.variantTypes
              ? JSON.stringify(product.variantTypes)
              : "",
            VariantItems: product.variantItems
              ? JSON.stringify(product.variantItems)
              : "",
            VariantStock: product.variantStock
              ? JSON.stringify(product.variantStock)
              : "",
            Images: (product.images || [])
              .map((img) => img.url)
              .join(", "),
            Shop: product.shop ? product.shop.toString() : "",
            SoldOut: product.sold_out,
            CreatedAt: product.createdAt
              ? new Date(product.createdAt).toISOString()
              : "",
              MinimumOrderQuantity: product.minimumOrderQuantity,
              ServiceableLocation: product.serviceableLocation,
          };

          // Insert quantity slabs
          if (product.quantitySlabs && product.quantitySlabs.length > 0) {
            product.quantitySlabs.forEach((slab, index) => {
              const slabNum = index + 1;
              rowData[`QuantitySlab${slabNum}_Min`] = slab.min;
              rowData[`QuantitySlab${slabNum}_Max`] = slab.max;
              rowData[`QuantitySlab${slabNum}_OriginalPrice`] =
                slab.originalPrice;
              rowData[`QuantitySlab${slabNum}_DiscountPrice`] =
                slab.discountPrice;
              rowData[`QuantitySlab${slabNum}_TotalPrice`] =
                slab.totalPrice || "";
            });

            // Fill the remaining slabs columns if needed
            for (let i = product.quantitySlabs.length + 1; i <= maxSlabs; i++) {
              rowData[`QuantitySlab${i}_Min`] = "";
              rowData[`QuantitySlab${i}_Max`] = "";
              rowData[`QuantitySlab${i}_OriginalPrice`] = "";
              rowData[`QuantitySlab${i}_DiscountPrice`] = "";
              rowData[`QuantitySlab${i}_TotalPrice`] = "";
            }
          } else {
            // No slabs => fill all slab columns with empty strings
            for (let i = 1; i <= maxSlabs; i++) {
              rowData[`QuantitySlab${i}_Min`] = "";
              rowData[`QuantitySlab${i}_Max`] = "";
              rowData[`QuantitySlab${i}_OriginalPrice`] = "";
              rowData[`QuantitySlab${i}_DiscountPrice`] = "";
              rowData[`QuantitySlab${i}_TotalPrice`] = "";
            }
          }

          return rowData;
        });

        // 5) Generate and download Excel
        const worksheet = XLSX.utils.json_to_sheet(dataToExport, {
          header: allColumns,
        });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Products");

        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const fileName = `Products_${seller._id}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        saveAs(blob, fileName);

        toast.success("Products downloaded successfully!");
      } else {
        toast.error("No products available to download.");
      }
    } catch (error) {
      console.error("Error downloading products:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to download products.";
      toast.error(errorMessage);
    }
  };

  // ======================
  // === DataGrid setup ===
  // ======================
  const columns = [
    { field: "id", headerName: "Product Id", minWidth: 150, flex: 0.7 },
    { field: "name", headerName: "Name", minWidth: 180, flex: 1.4 },
    { field: "price", headerName: "Price", minWidth: 200, flex: 0.6 },
    {
      field: "Stock",
      headerName: "Stock",
      minWidth: 80,
      flex: 0.5,
      renderCell: (params) => <StockCell {...params} />,
    },
    {
      field: "sold",
      headerName: "No of items sold",
      type: "number",
      minWidth: 150,
      flex: 0.6,
    },
    {
      field: "category",
      headerName: "Category",
      minWidth: 150,
      flex: 0.6,
    },
    {
      field: "gstPercentage",
      headerName: "GST Percentage",
      minWidth: 100,
      flex: 0.6,
    },
    {
      field: "Preview",
      flex: 0.8,
      minWidth: 80,
      headerName: "",
      type: "number",
      sortable: false,
      renderCell: (params) => (
        <Link to={`/product/${params.id}`}>
          <Button>
            <AiOutlineEye size={20} />
          </Button>
        </Link>
      ),
    },
    {
      field: "Edit",
      flex: 0.8,
      minWidth: 70,
      headerName: "",
      type: "number",
      sortable: false,
      renderCell: (params) => (
        <Link to={`/edit-product/${params.id}`}>
          <Button>
            <AiOutlineEdit size={20} />
          </Button>
        </Link>
      ),
    },
    {
      field: "Delete",
      flex: 0.8,
      minWidth: 120,
      headerName: "",
      type: "number",
      sortable: false,
      renderCell: (params) => (
        <Button
          onClick={() => {
            setProductToDelete(params.row);
            setOpenDeleteDialog(true);
          }}
        >
          <AiOutlineDelete size={20} />
        </Button>
      ),
    },
  ];

  // Convert each product to a row
  const rows = products.map((item) => {
    // Helper to get price range text
    const getPriceRange = () => {
      if (item.quantitySlabs && item.quantitySlabs.length > 0) {
        const prices = item.quantitySlabs
          .map((slab) => slab.discountPrice)
          .filter((price) => !isNaN(price));
        if (prices.length > 0) {
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);
          return `Rs ${minPrice.toFixed(2)} to Rs ${maxPrice.toFixed(2)}`;
        }
      }
      return `Rs ${item.discountPrice}`;
    };

    let stockDisplay = "";
    if (item.hasVariants) {
      stockDisplay = "Variant Stock";
    } else {
      if (item.stockAvailability === "All time stock available") {
        stockDisplay = "All Time Available";
      } else if (item.stockAvailability === "Limited stock available") {
        stockDisplay = `Limited Stock (${item.stock})`;
      } else if (item.stockAvailability === "Out of stock") {
        stockDisplay = "Out of Stock";
      } else {
        stockDisplay = item.stock;
      }
    }

    return {
      id: item._id,
      name: item.name,
      price: getPriceRange(),
      Stock: stockDisplay,
      sold: item.sold_out,
      category: item.category,
      gstPercentage: item.gstPercentage,
      hasVariants: item.hasVariants,
      variantStock: item.variantStock,
      stockStatus: item.stockStatus,
      stock: item.stock,
      stockAvailability: item.stockAvailability,
    };
  });

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="w-full mx-8 pt-1 mt-10 bg-white">
          {/* Header with Buttons */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">
              Products of {seller.name || "Your Shop"}
            </h2>
            <div className="flex space-x-2">
              {/* Hybrid Download Button */}
              <Button
                variant="contained" 
                color="primary"
                onClick={handleDownloadAllProductsHybrid}
              >
                Download All Products
              </Button>

              {/* Bulk Upload Button */}
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setBulkActionType("upload");
                  setBulkPopupOpen(true);
                }}
              >
                Bulk Upload
              </Button>

              {/* Bulk Edit Button */}
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setBulkActionType("edit");
                  setBulkPopupOpen(true);
                }}
              >
                Bulk Edit
              </Button>

              {/* Delete Multiple Button */}
              <Button variant="contained" color="error" onClick={handleDeleteMultiple}>
                Delete Selected
              </Button>
            </div>
          </div>

          {/* DataGrid with checkboxes */}
          <div className="datagrid-scroll-wrapper">
            <div className="table-inner-container" style={{ minWidth: "900px" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                autoHeight
                checkboxSelection
                disableSelectionOnClick
                rowSelectionModel={selectedProductIds}
                onRowSelectionModelChange={(ids) => setSelectedProductIds(ids)}
                filterModel={filterModel}
                onFilterModelChange={(newFilterModel) => {
                  setFilterModel(newFilterModel);
                  sessionStorage.setItem('shopAllProductsFilterModel', JSON.stringify(newFilterModel));
                }}
                sortModel={sortModel}
                onSortModelChange={(newSortModel) => {
                  setSortModel(newSortModel);
                  sessionStorage.setItem('shopAllProductsSortModel', JSON.stringify(newSortModel));
                }}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-virtualScroller": {
                    overflow: "visible !important",
                  },
                }}
              />
            </div>
          </div>

          {/* SINGLE Delete Confirmation Dialog */}
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
          >
            <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
            <DialogContent>
              Are you sure you want to delete the product "
              {productToDelete?.name}"?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
                Cancel
              </Button>
              <Button onClick={handleDelete} color="error">
                Delete
              </Button> 
            </DialogActions>
          </Dialog>

          {/* MULTIPLE Delete Confirmation Dialog */}
          <Dialog
            open={openDeleteMultipleDialog}
            onClose={() => setOpenDeleteMultipleDialog(false)}
            aria-labelledby="delete-multiple-dialog-title"
            aria-describedby="delete-multiple-dialog-description"
          >
            <DialogTitle id="delete-multiple-dialog-title">
              Confirm Deletion
            </DialogTitle>
            <DialogContent>
              {selectedProductIds.length === 0 ? (
                <p>No products selected.</p>
              ) : (
                <p>
                  Are you sure you want to delete{" "}
                  <strong>{selectedProductIds.length}</strong> selected product
                  {selectedProductIds.length > 1 ? "s" : ""}?
                </p>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenDeleteMultipleDialog(false)}
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={confirmDeleteMultiple} color="error">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* BULK Upload/Edit Confirmation Dialog */}
          <Dialog
            open={bulkPopupOpen}
            onClose={() => setBulkPopupOpen(false)}
            aria-labelledby="bulk-action-dialog-title"
            aria-describedby="bulk-action-dialog-description"
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle id="bulk-action-dialog-title">
              {bulkActionType === "upload"
                ? "Bulk Upload Products"
                : "Bulk Edit Products"}
            </DialogTitle>
            <DialogContent>
              {!isBulkLoading ? (
                <>
                  <p>
                    Bulk upload or Bulk edit Excel should be in the format as
                    Downloaded Products or else it will lead to corrupted data.
                    Get help from Hawler while bulk upload or bulk edit, or else
                    don't use these options without proper instruction.
                  </p>
                  <br />
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        setSelectedFile(e.target.files[0]);
                      }
                    }}
                    disabled={isBulkLoading}
                  />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center p-4">
                  <CircularProgress />
                  <p className="mt-2 text-center">
                    {bulkActionType === "upload"
                      ? "Uploading products. Please do not reload or refresh."
                      : "Editing products. Please do not reload or refresh."}
                  </p>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              {!isBulkLoading && (
                <>
                  <Button onClick={() => setBulkPopupOpen(false)} color="primary">
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (!selectedFile) {
                        toast.error("Please select an Excel file.");
                        return;
                      }
                      handleBulkAction();
                    }}
                    color="secondary"
                    variant="contained"
                  >
                    {bulkActionType === "upload" ? "Upload" : "Edit"}
                  </Button>
                </>
              )}
              <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isBulkLoading}
              >
                {/* Spinner displayed here if needed */}
              </Backdrop>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </>
  );
};

// ======================
// === StockCell comp ===
// ======================
const StockCell = (params) => {
  const { value, row } = params;
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setIsHovered(true);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setIsHovered(false);
  };

  const open = Boolean(anchorEl);
  const isMobile = window.innerWidth <= 600;

  return (
    <div
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      style={{
        position: "relative",
        backgroundColor: isHovered ? "#f9f9f9" : "transparent",
        cursor: row.hasVariants ? "pointer" : "default",
        padding: "4px",
      }}
    >
      {value}
      {row.hasVariants && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          disableRestoreFocus
          PaperProps={{
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose,
            style: {
              maxWidth: isMobile ? "150px" : "200px",
            },
          }}
        >
          <div
            style={{
              padding: isMobile ? 3 : 5,
              maxHeight: isMobile ? 80 : 100,
              overflowY: "auto",
              fontSize: isMobile ? "10px" : "12px",
            }}
          >
            {row.variantStock.map((variant, index) => (
              <div key={index}>
                <strong>{variant.combination}</strong>:{" "}
                {variant.availability === "All time stock available"
                  ? "All Time Available"
                  : variant.availability === "Out of stock"
                  ? "Out of Stock"
                  : variant.availability === "Limited stock available"
                  ? `Limited Stock (${variant.stock})`
                  : "Unknown Status"}
              </div>
            ))}
          </div>
        </Popover>
      )}
    </div>
  );
};

export default AllProducts;
